'use client';

import React from 'react';
import { cn } from '@/utils';
import { APP_EXTERNAL_LINKS } from '@/constants';

export interface AuthLinksProps extends React.HTMLAttributes<HTMLElement> {
  /** Array of link objects with label, href, and optional onClick */
  links?: typeof APP_EXTERNAL_LINKS;
  /** Whether to show the separator between links */
  showSeparator?: boolean;
  /** Custom separator text */
  separator?: string;
  /** Custom styling classes */
  className?: string;
}

/**
 * AuthLinks Component
 *
 * Displays a horizontal list of footer/auth links with separators.
 * Responsive design that stacks links vertically on small screens.
 * Based on the Figma design system with proper typography and colors.
 */
export const AuthLinks: React.FC<AuthLinksProps> = ({
  links = APP_EXTERNAL_LINKS,
  showSeparator = true,
  separator = '|',
  className,
  ...props
}) => {
  /**
   * Container classes for the ul element
   */
  const containerClasses = cn(
    // Base styles matching Figma design
    ['flex', 'flex-wrap', 'items-center', 'justify-center', 'max-xs:gap-2'],
    ['text-sm', 'leading-5', 'text-neutral-gray-700'],
    ['font-text', 'font-normal'],
    className,
  );

  /**
   * Link classes for each anchor element
   */
  const linkClasses = cn(
    'cursor-pointer',
    ['text-sm', 'leading-5'],
    ['text-neutral-gray-700', 'underline', 'decoration-solid'],
    ['hover:text-neutral-black', 'transition-colors', 'duration-200'],
    [
      'focus-visible:text-neutral-black',
      'focus-visible:outline-none',
      'focus-visible:ring-2',
      'focus-visible:ring-brand-primary',
      'focus-visible:ring-offset-2',
      'rounded-sm',
    ],
  );

  /**
   * Separator classes for the separator span elements
   */
  const separatorClasses = cn(
    ['text-neutral-gray-700', 'select-none'],
    ['hidden', 'xs:inline'], // Hide separators on mobile, show on desktop
    ['text-sm', 'leading-5'],
    ['mx-4'],
  );

  const renderLink = (link: (typeof links)[number], index: number) => {
    const linkElement = (
      <a
        className={linkClasses}
        href={link.href}
        key={`link-${index}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        {link.label}
      </a>
    );

    return (
      <li key={`fragment-${index}`}>
        {linkElement}
        {showSeparator && index < links.length - 1 && (
          <span className={separatorClasses} key={`sep-${index}`}>
            {separator}
          </span>
        )}
      </li>
    );
  };

  return (
    <ul className={containerClasses} {...props}>
      {links.map((link, index) => renderLink(link, index))}
    </ul>
  );
};

AuthLinks.displayName = 'AuthLinks';

export default AuthLinks;
